import React, { useState, useRef } from "react";
import Banner from "../Components/Banner";
import { contactPageData } from "../Constants/data.jsx";
import { Helmet } from "react-helmet-async";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";

const ContactUs = () => {
  const RECAPTCHA_SITE_KEY = "6LfTOPoqAAAAALiP94ZP6TEYP5XiTsKjvr7dpYh9";

  const SERVICE_ID = "service_rhr64ke";
  const TEMPLATE_ID = "template_xxz6bb4";
  const PUBLIC_KEY = "kMBtUuNkVAxKQH9ct";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    recaptcha: "",
  });

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      recaptcha: name === "message" ? "" : prev.recaptcha,
    }));

    if (name === "message" && value.trim().length > 0) {
      setShowCaptcha(true);
    } else if (name === "message" && value.trim().length === 0) {
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const onCaptchaChange = (value) => {
    if (value) {
      setErrors((prev) => ({
        ...prev,
        recaptcha: "",
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required.";
      valid = false;
    } else if (!nameRegex.test(formData.firstName.trim())) {
      newErrors.firstName = "First Name can only contain letters.";
      valid = false;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required.";
      valid = false;
    } else if (!nameRegex.test(formData.lastName.trim())) {
      newErrors.lastName = "Last Name can only contain letters.";
      valid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
      valid = false;
    } else if (!phoneRegex.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number (10 digits).";
      valid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
      valid = false;
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long.";
      valid = false;
    }

    if (formData.message.trim().length > 0) {
      const recaptchaValue = recaptchaRef.current?.getValue();
      if (!recaptchaValue) {
        newErrors.recaptcha = "Please verify that you are not a robot.";
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const templateParams = {
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
      };

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

      setShowSuccess(true);

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });

      setErrors({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        recaptcha: "",
      });

      setShowCaptcha(false);

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("EmailJS Error:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0">
        <Banner bannerData={contactPageData} />

        <section>
          <div className="mt-0 mt-md-5 pt-5">
            <div className="container">
              <div className="row">
                <div className="col-4 mb-0 md-mb-3">
                  <a
                    href="mailto:secretary@sydneysriayyappa.com"
                    className="card contact-card h-100 p-3 shadow-sm-md-up text-decoration-none text-reset"
                  >
                    <div className="d-flex justify-content-center mt-3">
                      <i className="bi bi-envelope-fill display-3 heading"></i>
                    </div>
                    <h5 className="text-center mt-2 text-dark d-none d-md-block">
                      Email US
                    </h5>
                    <h5 className="text-center mt-2 text-dark small d-md-none">
                      Email US
                    </h5>
                    <p className="text-center text-muted mt-2 d-none d-lg-block">
                      secretary@sydneysriayyappa.com
                    </p>
                  </a>
                </div>

                <div className="col-4 mb-0 md-mb-3">
                  <a
                    href="tel:0296312800"
                    className="card contact-card h-100 p-3 shadow-sm-md-up text-decoration-none text-reset"
                  >
                    <div className="d-flex justify-content-center mt-3">
                      <i className="bi bi-telephone-fill display-3 heading"></i>
                    </div>
                    <h5 className="text-center mt-2 text-dark d-none d-md-block">
                      Phone
                    </h5>
                    <h5 className="text-center mt-2 text-dark small d-md-none">
                      Phone
                    </h5>
                    <p className="text-center text-muted mt-2 d-none d-lg-block">
                      0296312800
                    </p>
                  </a>
                </div>

                <div className="col-4 mb-0 md-mb-3">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=1%20Perry%20Street%2C%20Wentworthville%2C%20NSW%2C%20Australia%2C%20New%20South%20Wales"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card contact-card h-100 p-3 shadow-sm-md-up text-decoration-none text-reset"
                  >
                    <div className="d-flex justify-content-center mt-3">
                      <i className="bi bi-geo-alt-fill display-3 heading"></i>
                    </div>
                    <h5 className="text-center mt-2 text-dark d-none d-md-block">
                      Address
                    </h5>
                    <h5 className="text-center mt-2 text-dark small d-md-none">
                      Address
                    </h5>
                    <p className="text-center text-muted mt-2 d-none d-lg-block">
                      1 Perry Street, Wentworthville,
                      <br /> NSW, Australia, New South Wales
                    </p>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="container pt-3 mt-0 mt-md-5 pb-5">
            <form onSubmit={handleSubmit}>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="First Name*"
                  />
                  {errors.firstName && (
                    <small className="text-danger">{errors.firstName}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Last Name*"
                  />
                  {errors.lastName && (
                    <small className="text-danger">{errors.lastName}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Email Address*"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </div>

                <div className="col-md-6">
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Phone Number*"
                  />
                  {errors.phone && (
                    <small className="text-danger">{errors.phone}</small>
                  )}
                </div>
              </div>

              <div className="mb-3 position-relative col-12">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-control py-3"
                  rows="4"
                  placeholder="Write your complaints or suggestions..."
                  style={{ height: "150px" }}
                ></textarea>
                {errors.message && (
                  <small className="text-danger">{errors.message}</small>
                )}
              </div>

              {showCaptcha && (
                <div className="mb-3 d-flex justify-content-start">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={onCaptchaChange}
                  />
                  {errors.recaptcha && (
                    <small className="text-danger ms-3 align-self-center">
                      {errors.recaptcha}
                    </small>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-center justify-content-md-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-warning px-5 py-3 hover-button text-white fw-bold border-0 position-relative rounded-5"
                >
                  {loading ? "SENDING..." : "SEND"}
                </button>
              </div>

              {showSuccess && (
                <div
                  className="alert alert-success alert-dismissible fade show mt-4"
                  role="alert"
                >
                  <strong>Success!</strong> Your message has been sent
                  successfully.
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSuccess(false)}
                  ></button>
                </div>
              )}
            </form>
          </div>
        </section>

        <section>
          <div className="col-md-12">
            <div className="map-container w-100" style={{ height: "400px" }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.066233039712!2d150.96786357649273!3d-33.81060381604827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b12bd63e38aea17%3A0xe10180d0690ad68f!2sSydney%20Sri%20Ayyappa%20Swami%20Centre!5e0!3m2!1sen!2slk!4v1733479979243!5m2!1sen!2slk"
                width="100%"
                height="100%"
                style={{ boxShadow: "0 0 8px #a79d9d" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sydney Sri Ayyappa Swami Centre Location"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactUs;